// =============================================================================
// ExceptionTooltip - Popup dettagli per assenza/presenza aggiuntiva
// =============================================================================

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { CalendarPlus, Clock, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../../hooks/useDateLocale';
import { getReasonVisual } from '../utils/exceptionStyle';
import type { AvailabilityException } from '../../../utils/availabilityUtils';

interface ExceptionTooltipProps {
  exception: AvailabilityException;
  visible: boolean;
  anchorRef: React.RefObject<HTMLElement>;
}

export function ExceptionTooltip({ exception, visible, anchorRef }: ExceptionTooltipProps) {
  const { t } = useTranslation('settings');
  const locale = useDateLocale();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!visible || !anchorRef.current) {
      setPosition(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const tooltipWidth = 260;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = rect.right + 8;
    if (left + tooltipWidth > viewportWidth) {
      left = rect.left - tooltipWidth - 8;
    }
    left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));

    let top = rect.top;
    if (top + 220 > viewportHeight) {
      top = Math.max(8, viewportHeight - 220);
    }

    setPosition({ top, left });
  }, [visible, anchorRef]);

  if (!visible || !position) return null;

  const isAvailable = exception.isAvailable;
  const start = parseISO(exception.startDate.slice(0, 10));
  const end = parseISO(exception.endDate.slice(0, 10));
  const isSingleDay = exception.startDate.slice(0, 10) === exception.endDate.slice(0, 10);
  const dateLabel = isSingleDay
    ? format(start, 'd MMM yyyy', { locale })
    : `${format(start, 'd MMM', { locale })} → ${format(end, 'd MMM yyyy', { locale })}`;
  const timeLabel =
    exception.startTime && exception.endTime
      ? `${exception.startTime} – ${exception.endTime}`
      : t('exceptions.allDay');

  const visual = getReasonVisual(exception.reason);
  const accentColor = isAvailable ? '#10B981' : visual.color;
  const HeaderIcon = isAvailable ? CalendarPlus : visual.Icon;
  const headerLabel = exception.title || exception.reason?.name || t('exceptions.absence');
  const reasonLabel = exception.reason?.name ?? null;

  return createPortal(
    <div
      className="fixed pointer-events-none animate-in fade-in slide-in-from-left-2 duration-150"
      style={{ minWidth: '240px', maxWidth: '260px', top: position.top, left: position.left, zIndex: 9999 }}
    >
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        {/* Header colorato */}
        <div className="px-3 py-2 flex items-center gap-2" style={{ backgroundColor: accentColor }}>
          <HeaderIcon className="w-4 h-4 text-white" />
          <span className="font-semibold text-sm text-white truncate">{headerLabel}</span>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                isAvailable ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {isAvailable ? t('exceptions.presence') : t('exceptions.absence')}
            </span>
            {!isAvailable && reasonLabel && (
              <span className="text-xs text-gray-500">{reasonLabel}</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>
              {dateLabel} · {timeLabel}
            </span>
          </div>

          {exception.notes && (
            <div className="flex items-start gap-2 text-gray-600 pt-2 border-t border-gray-100">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs italic">{exception.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
